import React from "react";
import { Helmet } from "react-helmet";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageTitle } from "@/components/PageTitle";
import { CTASection } from "@/components/CTASection";
import { SectionTitle } from "@/components/SectionTitle";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { Syringe } from "lucide-react";

const TMJTMD: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>TMJ/TMD Treatment in Nagpur | Cost of TMJ/TMD Treatment in Nagpur</title>
        <meta
          name="description"
          content="Get effective TMJ/TMD treatment at Asrani Dental Clinic in Nagpur. Expert care for jaw pain and discomfort. Book your consultation today!"
        />
      </Helmet>

      <Header />

      <main className="pt-20">
        <PageTitle
          title="TMJ/TMD Treatment"
        />

        {/* Service Overview Section */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1">
                <h2 className="text-3xl font-bold mb-6 text-dental-text">
                  TMJ/TMD Treatment at Asrani Dental, Sadar
                </h2>
                <p className="mb-4">
                  TMJ (Temporomandibular Joint) disorders, also known as TMD, can cause significant pain and discomfort in your jaw and surrounding areas. At Asrani Dental Clinic in Nagpur, we offer specialized TMJ/TMD treatment to help relieve your symptoms and improve your quality of life.
                </p>
                <p className="mb-4">
                  Our experienced team uses the latest diagnostic tools and treatment methods to address various forms of TMJ disorders, from mild discomfort to severe pain and limited jaw movement.
                </p>
                <p className="mb-6">
                  We focus on non-invasive, conservative approaches whenever possible, tailoring treatments to your specific condition and needs.
                </p>
                <div className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                  <Syringe className="w-8 h-8 text-dental-highlight mr-4" />
                  <div>
                    <h3 className="font-semibold">Personalized TMJ/TMD Care</h3>
                    <p className="text-sm text-gray-600">Every TMJ case is unique. Our treatment plans are customized to address your specific symptoms and causes.</p>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <img
                  src="/lovable-uploads/tmj_tmd.jpg"
                  alt="TMJ/TMD Treatment"
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Treatment Features */}
        <section className="py-16">
          <div className="container-custom">
            <SectionTitle
              title="Our TMJ/TMD Treatment Features"
              subtitle="Comprehensive Care for Your Jaw Health"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Syringe className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Thorough Diagnosis</h3>
                <p>
                  Our detailed examination process includes clinical assessment, imaging, and analysis of your symptoms to accurately diagnose your TMJ condition.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Syringe className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Custom Oral Appliances</h3>
                <p>
                  We create specially designed mouthguards and splints to reduce pressure on your jaw joint and prevent grinding and clenching.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Syringe className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Physical Therapy</h3>
                <p>
                  We incorporate specialized exercises and techniques to strengthen jaw muscles and improve movement and function.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Syringe className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Pain Management</h3>
                <p>
                  Our comprehensive approach includes various pain relief techniques, from medications to relaxation strategies.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Syringe className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Lifestyle Guidance</h3>
                <p>
                  We provide advice on diet, stress reduction, and habits that can help manage and reduce TMJ symptoms.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Syringe className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Advanced Treatment Options</h3>
                <p>
                  For more complex cases, we offer specialized treatments and can coordinate with other specialists when necessary.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <SectionTitle
              title="Why Choose Us for TMJ/TMD Treatment"
              subtitle="Expert Care at Asrani Dental, Nagpur"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">1</span>
                  Specialized Expertise
                </h3>
                <p>
                  Our dentists have advanced training in diagnosing and treating TMJ disorders, ensuring you receive expert care.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">2</span>
                  Comprehensive Approach
                </h3>
                <p>
                  We address both the symptoms and underlying causes of your TMJ disorder for more effective, long-lasting relief.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">3</span>
                  Conservative Treatment
                </h3>
                <p>
                  We prioritize non-surgical approaches when possible, focusing on minimally invasive treatments that yield maximum results.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">4</span>
                  Patient Education
                </h3>
                <p>
                  We empower you with knowledge about your condition and self-care techniques that can help manage symptoms between visits.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <ServiceTestimonials
          testimonials={[
            {
              name: "Rajesh Sharma",
              comment: "After years of jaw pain and headaches, Dr. Asrani properly diagnosed my TMJ disorder. The treatment plan brought relief when nothing else had worked. I'm finally able to eat comfortably again!",
              rating: 5,
              image: "/placeholder.svg"
            },
            {
              name: "Priya Deshmukh",
              comment: "I was skeptical about finding relief for my jaw pain, but the custom nightguard and therapy exercises prescribed at Asrani Dental have made an incredible difference. The team really understands TMJ issues.",
              rating: 5,
              image: "/placeholder.svg"
            }
          ]}
          title="What Our TMJ/TMD Patients Say"
          subtitle="Real Experiences from Patients at Asrani Dental Clinic in Nagpur"
        />

        {/* FAQ Section */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <SectionTitle
              title="Frequently Asked Questions"
              subtitle="Common Questions About TMJ/TMD Treatment in Nagpur"
            />

            <div className="mt-12 max-w-3xl mx-auto">
              <FAQAccordion
                faqs={[
                  {
                    question: "What are the common symptoms of TMJ disorders?",
                    answer: "Common symptoms include jaw pain or tenderness, difficulty or pain when chewing, facial pain, clicking or popping sounds in the jaw joint, locking of the jaw joint, headaches, earaches, neck pain, and limited jaw movement."
                  },
                  {
                    question: "How are TMJ disorders diagnosed?",
                    answer: "Diagnosis typically involves a thorough examination of your jaw, observation of jaw movement, listening for sounds, and feeling the joint and muscles for tenderness. We may also use imaging tests like X-rays, CT scans, or MRIs to get a detailed view of your jaw joint and surrounding structures."
                  },
                  {
                    question: "Are TMJ treatments covered by insurance?",
                    answer: "Coverage varies widely depending on your insurance provider and plan. Some treatments may be covered, particularly if they're deemed medically necessary. Our administrative team can help you understand your coverage and payment options."
                  },
                  {
                    question: "How long does TMJ/TMD treatment take?",
                    answer: "Treatment duration varies significantly depending on the severity of your condition, the type of treatment needed, and how your body responds. Some patients find relief in a few weeks with conservative treatments, while more complex cases may require months of therapy or ongoing management."
                  },
                  {
                    question: "Can TMJ disorders go away on their own?",
                    answer: "Mild cases of TMJ disorders sometimes improve on their own with rest and self-care. However, moderate to severe cases typically require professional treatment to prevent worsening symptoms and potential complications."
                  }
                ]}
              />
            </div>
          </div>
        </section>

        <CTASection
          title="Experience Relief from TMJ Pain"
          description="Don't let jaw pain affect your quality of life. Schedule your TMJ/TMD consultation at Asrani Dental Clinic in Nagpur today."
          primaryButtonText="Book Your Consultation"
          primaryButtonLink="/dental-appointment-booking"
        />
      </main>

      <Footer />
    </>
  );
};

export default TMJTMD;
